'use client';

import { useState, useEffect } from 'react';
import { Modal, Stack, Group, Button, Select, Loader } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import axiosInstance from '@libs/axios';
import { notifications } from '@mantine/notifications';
import usePPJK from '@hooks/use-ppjk';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { JarvisText, JarvisDatePicker, JarvisInputLabel } from '@components/jarvis-ui';
import dayjs from 'dayjs';

const DATE_REQUIRED_COMPANY = 'PT ASTRA VISTEON INDONESIA';

interface DrafterOption {
  value: string;
  label: string;
}

interface CompanyOption {
  value: string;
  label: string;
}

interface DownloadReportModalProps {
  opened: boolean;
  onClose: () => void;
  /**
   * Branch selection to export, mirroring the listing's own filter: '' means
   * the caller's own branch, BRANCH_ALL means every branch. Without this the
   * export would always fall back to the caller's own branch, so an audit user
   * who had picked "Semua Cabang" on screen would silently download a narrower
   * workbook than the one they were looking at.
   */
  branchFilter?: string;
}

export default function DownloadReportModal({ opened, onClose, branchFilter = '' }: DownloadReportModalProps) {
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { nomorPpjk, isLoading: ppjkLoading } = usePPJK();
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [drafterOptions, setDrafterOptions] = useState<DrafterOption[]>([]);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [selectedDrafter, setSelectedDrafter] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [drafterSearchValue, setDrafterSearchValue] = useState('');
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const requiresDateRange = selectedCompany === DATE_REQUIRED_COMPANY;

  // Fetch drafter options
  const fetchDrafterOptions = async (ppjkNpwp: string) => {
    if (!ppjkNpwp) {
      console.log('No PPJK NPWP provided for drafter options');
      return;
    }

    try {
      console.log('Fetching drafter options with PPJK NPWP:', ppjkNpwp);
      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/drafter-names?ppjkNpwp=${ppjkNpwp}`);

      console.log('Drafter API response:', response.data);

      if (response.data?.success && response.data.data?.drafterNames) {
        const drafterOptions = response.data.data.drafterNames.map((name: string) => ({
          value: name,
          label: name,
        }));

        console.log('Setting drafter options:', drafterOptions);
        setDrafterOptions(drafterOptions);
      }
    } catch (error) {
      console.error('Error fetching drafter options:', error);
    }
  };

  // Fetch company options
  const fetchCompanyOptions = async (searchQuery = '', ppjkNpwpFilter = '') => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (ppjkNpwpFilter) params.append('ppjkNpwp', ppjkNpwpFilter);

      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/customer-names?${params.toString()}`);

      if (response.data?.success && response.data.data?.customerNames) {
        const companyOptions = response.data.data.customerNames.map((name: string) => ({
          value: name,
          label: name,
        }));

        setCompanyOptions(companyOptions);
      }
    } catch (error) {
      console.error('Error fetching company options:', error);
    }
  };

  // Load initial data when modal opens
  useEffect(() => {
    if (opened && !ppjkLoading) {
      setLoading(true);

      console.log('Loading data with PPJK NPWP:', nomorPpjk);

      // Fetch both options
      Promise.all([
        fetchCompanyOptions('', nomorPpjk || ''),
        nomorPpjk ? fetchDrafterOptions(nomorPpjk) : Promise.resolve(),
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [opened, nomorPpjk, ppjkLoading]);

  // Search handlers
  useEffect(() => {
    if (companySearchValue) {
      fetchCompanyOptions(companySearchValue, nomorPpjk || '');
    }
  }, [companySearchValue, nomorPpjk]);

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      setSelectedDrafter('');
      setSelectedCompany('');
      setDrafterSearchValue('');
      setCompanySearchValue('');
      setDrafterOptions([]);
      setCompanyOptions([]);
      setStartDate(null);
      setEndDate(null);
    }
  }, [opened]);

  const formatDateParam = (date: Date) => dayjs(date).format('DD-MM-YYYY');

  const appendDownloadParams = (params: URLSearchParams) => {
    if (selectedCompany) params.append('customerName', selectedCompany);
    if (selectedDrafter) params.append('drafterName', selectedDrafter);
    if (startDate) params.append('startDate', formatDateParam(startDate));
    if (endDate) params.append('endDate', formatDateParam(endDate));
    // Omitted when empty on purpose: the API reads an absent branch as the
    // caller's own, which is the same contract the listing uses.
    if (branchFilter) params.append('branch', branchFilter);
    params.append('limit', '500');
  };

  const hasDateRange = Boolean(startDate && endDate);
  const hasFilter = Boolean(selectedCompany || selectedDrafter || hasDateRange);

  const handleDownload = async () => {
    // Drafter is optional; require company, drafter, and/or date range
    if (!hasFilter) {
      notifications.show({
        title: 'Error',
        message: t('docpabean.errSelectCompanyDates'),
        color: 'red',
      });
      return;
    }

    if (requiresDateRange && !hasDateRange) {
      notifications.show({
        title: 'Error',
        message: t('docpabean.errDateRequiredCompany').replace('{company}', DATE_REQUIRED_COMPANY),
        color: 'red',
      });
      return;
    }

    if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
      notifications.show({ title: 'Error', message: t('docpabean.errEndBeforeStart'), color: 'red' });
      return;
    }

    setDownloadLoading(true);
    try {
      const params = new URLSearchParams();
      appendDownloadParams(params);

      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/export-history-status?${params.toString()}`, {
        responseType: 'blob', // Important for file downloads
      });

      // Create blob and download
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Generate filename
      const filename = `export_history_${selectedCompany ? `company_${selectedCompany}_` : ''}${selectedDrafter ? `drafter_${selectedDrafter}_` : ''}${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      notifications.show({ title: 'Success', message: t('docpabean.successReportDownloaded'), color: 'green' });
      onClose();
    } catch (error) {
      console.error('Error downloading report:', error);
      notifications.show({ title: 'Error', message: t('docpabean.errReportDownload'), color: 'red' });
    } finally {
      setDownloadLoading(false);
    }
  };

  const isValid = hasFilter && (!requiresDateRange || hasDateRange);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconDownload size={18} style={{ color: themeColors.text }} />
          <JarvisText size="md" fw={600}>
            {t('docpabean.downloadModalTitle')}
          </JarvisText>
        </Group>
      }
      size="md"
      centered
      overlayProps={{
        backgroundOpacity: 0.6,
        blur: 2,
      }}
      styles={{
        content: {
          background: themeColors.card,
          borderRadius: '8px',
          border: `1px solid ${themeColors.border}`,
          boxShadow: `0 10px 15px -3px ${themeColors.accent}20, 0 4px 6px -2px ${themeColors.accent}15`,
        },
        header: {
          borderBottom: `1px solid ${themeColors.border}`,
          paddingBottom: '0.75rem',
          marginBottom: '1rem',
          backgroundColor: themeColors.card,
        },
        body: {
          padding: '1.5rem',
          backgroundColor: themeColors.card,
        },
      }}
    >
      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="md" style={{ color: themeColors.primary }} />
          <JarvisText size="sm" c={themeColors.textSecondary}>
            {t('docpabean.loadingOptions')}
          </JarvisText>
        </Group>
      ) : (
        <Stack gap="lg">
          <JarvisText size="sm" c={themeColors.textSecondary}>
            {t('docpabean.filterHint')}
            {requiresDateRange
              ? ` ${t('docpabean.errDateRequiredCompany').replace('{company}', DATE_REQUIRED_COMPANY)}`
              : ''}
          </JarvisText>

          <Stack gap="md">
            <div>
              <JarvisText size="sm" fw={500} mb="xs">
                {t('docpabean.drafterName')}
              </JarvisText>
              <Select
                placeholder={t('docpabean.drafterPlaceholder')}
                data={drafterOptions}
                value={selectedDrafter || null}
                onChange={(value) => setSelectedDrafter(value || '')}
                searchable
                clearable
                nothingFoundMessage={t('docpabean.noDrafters')}
                maxDropdownHeight={200}
                styles={{
                  input: {
                    backgroundColor: themeColors.formFields,
                    borderColor: themeColors.border,
                    color: themeColors.text,
                    '&:focus': {
                      borderColor: themeColors.primary,
                    },
                  },
                  dropdown: {
                    backgroundColor: themeColors.card,
                    borderColor: themeColors.border,
                  },
                  option: {
                    color: themeColors.text,
                    '&[data-selected]': {
                      backgroundColor: `${themeColors.primary}20`,
                      color: themeColors.primary,
                    },
                    '&[data-hovered]': {
                      backgroundColor: `${themeColors.primary}10`,
                    },
                  },
                }}
              />
            </div>

            <div>
              <JarvisText size="sm" fw={500} mb="xs">
                {t('docpabean.companyName')}
              </JarvisText>
              <Select
                placeholder={t('docpabean.companyPlaceholder')}
                data={companyOptions}
                value={selectedCompany}
                onChange={(value) => setSelectedCompany(value || '')}
                searchable
                searchValue={companySearchValue}
                onSearchChange={setCompanySearchValue}
                clearable
                nothingFoundMessage={t('docpabean.noCompanies')}
                maxDropdownHeight={200}
                styles={{
                  input: {
                    backgroundColor: themeColors.formFields,
                    borderColor: themeColors.border,
                    color: themeColors.text,
                    '&:focus': {
                      borderColor: themeColors.primary,
                    },
                  },
                  dropdown: {
                    backgroundColor: themeColors.card,
                    borderColor: themeColors.border,
                  },
                  option: {
                    color: themeColors.text,
                    '&[data-selected]': {
                      backgroundColor: `${themeColors.primary}20`,
                      color: themeColors.primary,
                    },
                    '&[data-hovered]': {
                      backgroundColor: `${themeColors.primary}10`,
                    },
                  },
                }}
              />
            </div>

            <div>
              <JarvisInputLabel required={requiresDateRange}>{t('docpabean.startDate')}</JarvisInputLabel>
              <JarvisDatePicker
                placeholder={t('docpabean.selectStartDate')}
                valueFormat="DD-MM-YYYY"
                value={startDate}
                onChange={(val) => setStartDate(val)}
                clearable
                maxDate={endDate || undefined}
              />
            </div>

            <div>
              <JarvisInputLabel required={requiresDateRange}>{t('docpabean.endDate')}</JarvisInputLabel>
              <JarvisDatePicker
                placeholder={t('docpabean.selectEndDate')}
                valueFormat="DD-MM-YYYY"
                value={endDate}
                onChange={(val) => setEndDate(val)}
                clearable
                minDate={startDate || undefined}
              />
            </div>
          </Stack>

          <Group justify="flex-end" gap="sm" pt="md">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={downloadLoading}
              styles={{
                root: {
                  borderColor: themeColors.border,
                  color: themeColors.text,
                  '&:hover': {
                    backgroundColor: themeColors.buttonHover,
                  },
                },
              }}
            >
              {t('docpabean.cancel')}
            </Button>
            <Button
              onClick={handleDownload}
              loading={downloadLoading}
              disabled={!isValid}
              leftSection={<IconDownload size={16} />}
              styles={{
                root: {
                  backgroundColor: `${themeColors.primary} !important`,
                  borderColor: `${themeColors.primary} !important`,
                  color: `${themeColors.buttonText} !important`,
                  boxShadow: `0 5px 15px -5px ${themeColors.primary}30 !important`,
                  '&:hover': {
                    backgroundColor: `${themeColors.primaryHover} !important`,
                    borderColor: `${themeColors.primaryHover} !important`,
                    color: `${themeColors.buttonText} !important`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 20px -6px ${themeColors.primary}40 !important`,
                  },
                  '&:disabled': {
                    backgroundColor: `${themeColors.buttonHover}80 !important`,
                    borderColor: `${themeColors.buttonHover}80 !important`,
                    color: `${themeColors.textSecondary} !important`,
                    boxShadow: 'none !important',
                    transform: 'none !important',
                  },
                },
                inner: {
                  color: `${themeColors.buttonText} !important`,
                },
                label: {
                  color: `${themeColors.buttonText} !important`,
                },
              }}
            >
              {t('docpabean.downloadReport')}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
